import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";

import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { TranslateService } from "@ngx-translate/core";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import { GridOptions, RowNode } from "ag-grid-community/main";
import {
  ProfilePromiseActionService,
  ProfileSettingKeys,
  Temperature,
  UserTemperatureService,
} from "shared/cubiq-services";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

import { BfxUserSettingsService } from "../bfx-user-settings/bfx-user-settings.service";

import { SystemConfigPromiseService } from "../../shared/cubiq-services/index";
import { AlgoMonitorDefinitionFactory } from "../../shared/cubiq-services/model/AlgoMonitorDefinitionFactory";
import { BfxBlotterOrders } from "../bfx-blotter-orders/bfx-blotter-orders";
import { BfxMouseModifierKeyService } from "../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { BfxAlgoMonitorFilteringService } from "./services/bfx-algo-monitor-filter.service";
import { BfxAlgoMonitorService } from "./services/bfx-algo-monitor.service";

import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";
import { OrderPosition } from "../../shared/cubiq-services/model/orders/OrderPosition";
import { FillRenderer } from "../bfx-blotter-orders/renderers/FillRenderer";
import { FwdPtsRenderer } from "../bfx-blotter-orders/renderers/FwdPtsRenderer";
import { MarketMidRenderer } from "../bfx-blotter-orders/renderers/MarketMidRenderer";
import { OrderTypeRenderer } from "../bfx-blotter-orders/renderers/OrderTypeRenderer";
import { PercentAwayRenderer } from "../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { SpotTrackingRenderer } from "../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

import { ExtendedOrderTypeIdentifier } from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { FillPercentRenderer } from "app/bfx-blotter-orders/renderers/FillPercentRenderer";
import { BfxAlgoMonitorContextMenuService } from "./bfx-algo-monitor-contextmenu.service";

import { LoggerService } from "@bfx/logger";
import { BfxAlgoRiskTradeBlotterComponent } from "app/bfx-algo-monitor/bfx-algo-risk-trade-blotter.component";
import {
  AlgoFilterHelper,
  AlgoStateFilterType,
} from "../bfx-interface-controls/helpers/AlgoFilterHelper";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { BfxVwapOrdersService } from "app/bfx-vwap/bfx-vwap-orders.service";
import { BfxSplitterComponent } from "shared/base-components/bfx-splitter.component";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
const logger = LoggerService.for(__dirname);

const CONFIG_VERSION: string = "1.3";

const HIDE_TOGGLE_CLASS: string = "bfx-toggle-button-selected-subtle";
const SHOW_TOGGLE_CLASS: string = "bfx-toggle-button-unselected-subtle";

/**
 * Presentation model type
 */
type AMData = {
  /** Is trades blotter visible? */
  showTradesBlotter: boolean;
  /** Icon used in show/hide button */
  showHideIconClass: string;
  /* Toggle button class */
  showHideToggleClass: string;

  /** Label for show/hide button */
  showHideLabel: string;

  canPauseAll: boolean;
  canCancelAll: boolean;

  algoFilterType: string;

  /** Label for pause All button */
  pauseAllLabel: string;
  /** Label for cancel All Button */
  cancelAllLabel: string;

  /** Array of initial view sizes stored as relative view size %s */
  initSizes: number[];

  /** global lookups required  */
  reference: {
    iconClasses: {
      directionUp: string;
      directionDown: string;
    };

    labels: {
      show: string;
      hide: string;
    };
  };
};

/**
 * The settings are stored for this view. Changes to this
 * show respect previously stored setting where possible
 */
type ConfigSettings = {
  algoFilterType: string;
  showTradesBlotter: boolean;
  separatorSizes: number[];
  configVersion: string;
};

@Component({
  selector: "bfx-algo-monitor",
  templateUrl: "./html/bfx-algo-monitor.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxAlgoMonitor), BfxAlgoMonitorService],
  standalone: false,
})
export class BfxAlgoMonitor extends BfxBlotterOrders implements OnInit {
  @ViewChild("tableTarget", { static: true }) blotterView: ElementRef;

  @ViewChild("splitter", { static: true }) splitter: BfxSplitterComponent;

  /** Risk trade blotter in template */
  @ViewChild("riskTradeBlotter", { static: false })
  public riskTradeBlotter: BfxAlgoRiskTradeBlotterComponent;

  public extendedTypeId: ExtendedOrderTypeIdentifier = ExtendedOrderTypeIdentifier.ALGO;

  protected filteredOrderIds: Set<string> = new Set();
  protected filteredOrdersPositions: Map<string, OrderPosition> = new Map();

  public currencyPairTooltip: string | number;

  /** Order state filter applied to all algos */
  private stateFilter: AlgoStateFilterType = AlgoStateFilterType.ALL;

  checkQueueTimeoutId: NodeJS.Timeout;

  public amData: AMData;

  /** Ids currently selected  */
  private selectedOrderIds: string[];
  /** Ids bound to Risk Trade blotter so only set when blotter is visible */
  public tradeOrderIds: string[];

  /** Quick check if Algo types supported */
  private hasAlgoFilter: boolean = true;

  /** Quick check if BmAlgo types supported */
  private hasBMAlgoFilter: boolean = true;

  private hasCancelAlgos: boolean;
  private hasCancelBMAlgos: boolean;
  private hasPauseAll: boolean;

  public algoFilterTypes = [];

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxAlgoMonitorService,
    private algoContextMenuService: BfxAlgoMonitorContextMenuService,
    zone: NgZone,
    // see _tableDefinitionFactory
    factory: AlgoMonitorDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxAlgoMonitorFilteringService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    _sysConfigService: SystemConfigPromiseService,
    userSettingsService: BfxUserSettingsService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected userTempService: UserTemperatureService,
    protected vwapOrdersService: BfxVwapOrdersService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      algoContextMenuService,
      zone,
      factory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      _sysConfigService,
      userSettingsService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      userTempService,
      vwapOrdersService,
      vwapInfo,
    );

    this.initializeViewData();

    this.supportsAggregation = false;
    NativeDesktopSupport.prepareGridOptions(this.gridOptions, BfxBlotterElement.nativeElement);

    Promise.all([
      this.algoContextMenuService.canCancelAlgos(),
      this.algoContextMenuService.canCancelBMAlgos(),
      this.algoContextMenuService.canPauseAll(),
    ]).then(([cancelAlgos, cancelBMAlgos, pauseAll]) => {
      this.hasCancelAlgos = cancelAlgos;
      this.hasCancelBMAlgos = cancelBMAlgos;
      this.hasPauseAll = pauseAll;

      this.updateActionAvailability();
    });
  }

  public ngOnInit(): void {
    if (!this.isRestoredFromConfig) {
      // If we have not already restored from GL, then we restore from last default
      this.loadAlgoMonitorSettings().then((settings) => this.applyAlgoMonitorSettings(settings));
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    clearTimeout(this.checkQueueTimeoutId);
  }

  /**
   * Setup initial default view state, and cache all reqd translations etc
   */
  private initializeViewData() {
    this.showSearch = true;

    const labels = {
      show: this.translationService.instant("algo_monitor.button.show_trades"),
      hide: this.translationService.instant("algo_monitor.button.hide_trades"),
    };
    const iconClasses = {
      directionUp: "bfx-button-icon icon_ion_visibility_off_filled",
      directionDown: "bfx-button-icon icon_ion_visibility_filled",
    };

    this.algoFilterTypes = [
      this.translationService.instant("algo_monitor.filter_all"),
      this.translationService.instant("algo_monitor.filter_algo"),
      this.translationService.instant("algo_monitor.filter_bmalgo"),
    ];

    this.amData = {
      // Trades hidden by default
      showTradesBlotter: false,
      showHideIconClass: iconClasses.directionDown,
      showHideToggleClass: HIDE_TOGGLE_CLASS,
      showHideLabel: labels.hide,

      algoFilterType: this.algoFilterTypes[0],

      canPauseAll: false,
      canCancelAll: false,

      pauseAllLabel: this.translationService.instant("algo_monitor.pause_all"),
      cancelAllLabel: this.translationService.instant("algo_monitor.cancel_all"),

      initSizes: [70, 30],

      reference: {
        iconClasses,
        labels,
      },
    };
  }

  getVwapSource(): string {
    return "Algo Monitor";
  }

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    fillRenderer: FillRenderer,
    fillPercRenderer: FillPercentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    fwdPtsRenderer: FwdPtsRenderer,
  };

  protected getConfigToSaveAsDefault(): any {
    let defColAndSort = super.getConfigToSaveAsDefault();
    defColAndSort["stateFilter"] = this.stateFilter;
    defColAndSort.algoMonitorSettings = this.createAlgoMonitorSettings();
    return defColAndSort;
  }

  public restoreFromConfiguration(config: any): void {
    try {
      super.restoreFromConfiguration(config);
      if (config != null && config.stateFilter != null) {
        this.stateFilter = config.stateFilter;
      }
      if (config != null && config.algoMonitorSettings != null) {
        this.applyAlgoMonitorSettings(config.algoMonitorSettings);
      }
      this.refreshPanelVisibility();
    } catch (e) {
      logger.out.error(`${this.constructor.name}: Failed to restore config: ${e}`, e);
    }
  }

  public getComponentConfiguration(): any {
    let configuration: any = super.getComponentConfiguration();
    configuration.stateFilter = this.stateFilter;
    configuration.algoMonitorSettings = this.createAlgoMonitorSettings();
    return configuration;
  }

  public getBlotterTypeStr(): string {
    let type: string = super.getBlotterTypeStr();
    return type + "_ALGOMON";
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  protected checkIfNeedMarketBar(): void {}

  public showSettings(event: Event): void {
    this.layoutService.showSettings("AlgoMonitor", event);
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions = super.createsGridOptions();

    if (gridOptions?.rowClassRules != null) {
      gridOptions.rowClassRules.cancelled = (params) => {
        return CubiqTS.OrderState.CANCELLED == params.data.orderState;
      };

      gridOptions.rowClassRules.rejected = (params) => {
        return (
          CubiqTS.OrderState.REJECTED == params.data.orderState ||
          CubiqTS.OrderState.EXPIRED == params.data.orderState
        );
      };

      gridOptions.rowClassRules.filled = (params) => {
        return CubiqTS.OrderState.FILLED == params.data.orderState;
      };
    }

    return gridOptions;
  }

  public override hasCellRenderedSelection(): boolean {
    return true;
  }

  protected override checkPrimaryHotStyle(_params): boolean {
    return false;
  }
  protected override checkPrimaryWarmStyle(_params): boolean {
    return false;
  }
  protected override checkPrimaryColdStyle(_params): boolean {
    return true;
  }

  protected override checkHotStyle(_params): boolean {
    return false;
  }
  protected override checkWarmStyle(_params): boolean {
    return false;
  }
  protected override checkColdStyle(_params): boolean {
    return true;
  }

  public override doesTemperatureFilterPass(
    _blotterTemperature: Temperature,
    _rawData: LimitOrder,
  ): boolean {
    return true;
  }

  /**
   * Returns the row items currently selected aside from the ones
   * that have IDs filtered out by the isValidRowID method
   */
  protected override getSelectedRows(): Array<any> {
    let selectedRows = this.gridOptions.api.getSelectedRows();
    let validatedRows = new Map<string, any>();

    if (selectedRows != null && selectedRows.length > 0) {
      selectedRows.forEach((row: any) => {
        if (
          row[this.tableDefinition.primaryKey] != null &&
          this.isValidRowID(row[this.tableDefinition.primaryKey])
        ) {
          validatedRows.set(row[this.tableDefinition.primaryKey], row);
        }
      });
    }

    return Array.from(validatedRows.values());
  }

  public override shouldShowTemperatureFilter(): boolean {
    return false;
  }

  public shouldShowStateFilter(): boolean {
    return false;
  }

  public set stateFilterType(stateSelected: AlgoStateFilterType) {
    this.stateFilter = stateSelected;
    if (this.gridOptions.api) {
      this.gridOptions.api.onFilterChanged();
      this.afterFilteringCompleted();
    }
    this.updateActionAvailability();
  }

  public get stateFilterType(): AlgoStateFilterType {
    return this.stateFilter;
  }

  /**
   *Check single grid for filter - after default filter, also check order state, and order type
   * @param node
   * @returns
   */
  protected checkExternalFilterVisibility(node: RowNode): boolean {
    let result = super.checkExternalFilterVisibility(node);

    return (
      result &&
      AlgoFilterHelper.doesStateFilterPass(this.stateFilter, node.data) &&
      this.doesOrderTypeFilterPass(node.data)
    );
  }

  /**
   * Apply view-specific filter on blotter data that checks the algo type
   * @param node
   * @returns
   */
  private doesOrderTypeFilterPass(algo: CubiqTS.OrderDataImpl): boolean {
    if (algo.orderType === CubiqTS.OrderType.ALGO) {
      return this.hasAlgoFilter;
    }
    return this.hasBMAlgoFilter;
  }

  public getTabIdentifier(): string {
    return "BfxAlgoMonitor";
  }

  /**
   * Toggle visibility of Risk Trades panel
   */
  toggleTradesPanel() {
    this.amData.showTradesBlotter = !this.amData.showTradesBlotter;
    this.refreshPanelVisibility();
  }

  /**
   * Update the action bar and splitter depending on state of show trades blotter
   */
  private refreshPanelVisibility() {
    if (this.amData.showTradesBlotter) {
      this.amData.showHideIconClass = this.amData.reference.iconClasses.directionDown;
      this.amData.showHideLabel = this.amData.reference.labels.hide;
      this.amData.showHideToggleClass = HIDE_TOGGLE_CLASS;
      this.tradeOrderIds = this.selectedOrderIds;
      this.splitter.restore();
    } else {
      this.amData.showHideIconClass = this.amData.reference.iconClasses.directionUp;
      this.amData.showHideLabel = this.amData.reference.labels.show;
      this.amData.showHideToggleClass = SHOW_TOGGLE_CLASS;
      this.splitter.maximizePanel(0);

      if (this.riskTradeBlotter) {
        this.riskTradeBlotter.clearSelection();
      }
    }
  }

  /**
   * Key used to identify this view's profile settings
   * @returns
   */
  private getAlgoMonitorProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileAlgoMonitor}`;
  }

  /**
   * Push the persistent view settings out to the Profile service -
   */
  private saveAlgoMonitorSettings(): void {
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
    const configSettings = this.createAlgoMonitorSettings();
    profileSettings.push(
      this.userProfileService.createProfileSettingObject(
        this.getAlgoMonitorProfileSettingKey(),
        JSON.stringify(configSettings),
      ),
    );
    this.profileActionService.updateProfile(profileSettings, false);
  }

  /**
   * Load and resolve the config settings for this view - called if restoring default view settings
   * @returns
   */
  private loadAlgoMonitorSettings(): Promise<ConfigSettings> {
    const profileConfigKey = this.getAlgoMonitorProfileSettingKey();

    return new Promise<ConfigSettings>((res) => {
      this.profileActionService.getProfileConfig(profileConfigKey).then(
        (value: string) => {
          if (typeof value == "string") {
            let config: ConfigSettings;
            try {
              config = JSON.parse(value);
            } catch (error) {
              logger.out.error("Error parsing algo monitor config");
              config = null;
            }
            res(config);
          }
        },
        () => {
          res(null);
        },
      );
    });
  }

  /**
   * Create persistent presentation settings
   * @returns
   */
  private createAlgoMonitorSettings() {
    const configSettings: ConfigSettings = {
      algoFilterType: this.amData.algoFilterType,
      showTradesBlotter: this.amData.showTradesBlotter,
      separatorSizes: this.splitter.panelSizes,
      configVersion: CONFIG_VERSION,
    };

    return configSettings;
  }

  /**
   *  Apply currently stored config settings.
   * @param config
   */
  private applyAlgoMonitorSettings(config: ConfigSettings) {
    if (config && config.configVersion === CONFIG_VERSION) {
      if (typeof config.algoFilterType === "string") {
        this.algoFilterType = config.algoFilterType;
      }
      if (typeof config.showTradesBlotter === "boolean") {
        this.amData.showTradesBlotter = config.showTradesBlotter;
      }

      if (Array.isArray(config.separatorSizes) && config.separatorSizes.length == 2) {
        this.amData.initSizes = config.separatorSizes;
      }
    }
    this.refreshPanelVisibility();
  }

  /**
   * Once risks are selected, their VWAP text will overwrite any VWAP published for the orders.
   * We to monitor the number of selected Risk Trades - when none are selected, we
   * republish the latest order-level VWAP total
   *
   * @param riskCount
   */
  public onRiskTradeSelectionChange(riskCount: number) {
    if (riskCount == 0) {
      this.generateAndPublishVWAPString();
    } else {
      const riskVwap = this.riskTradeBlotter.vwapFooterText;
      if (riskVwap) {
        this.publishVWAP(riskVwap, this.riskTradeBlotter.getVwapSource());
      }
    }
  }

  /**
   * Forward on any changed to selected orders to property bound to the RiskTrade blotter
   */
  public override onSelectionChanged(): void {
    super.onSelectionChanged();
    const orderIds = this.getOrdersIdsOfSelectedRows();
    this.selectedOrderIds = orderIds;
    if (this.amData.showTradesBlotter) {
      // No point in triggering full filter etc if blotter is not visible
      this.tradeOrderIds = orderIds;
    }
  }

  /**
   * Once risks are selected, their VWAP text will overwrite any VWAP published for the orders.
   * We to monitor the number of selected Risk Trades - when none are selected, we
   * republish the latest order-level VWAP total
   *
   * @param tradeVwap
   */
  public onRiskTradeVWAPChange(tradeVwap: string) {
    this.publishVWAP(tradeVwap, this.riskTradeBlotter.getVwapSource());
  }

  public override isExternalFilterPresent(): boolean {
    return true;
  }

  /**
   * Override to also save general layout and also layout of Risk trade blotter
   */
  public override saveAsDefault(): void {
    super.saveAsDefault();
    this.saveAlgoMonitorSettings();

    if (this.riskTradeBlotter) {
      this.riskTradeBlotter.saveAsDefault();
    }
  }

  /**
   * Get all visible orders and send then to the service whjich will extract pausable orders and
   * forward them to the Pause Action
   */
  public pauseAll() {
    const orders = [];
    this.gridOptions.api.forEachNodeAfterFilter((rowNode) => orders.push(rowNode.data));
    this.algoContextMenuService.pauseAllOrders(orders);
  }

  /**
   * Get all visible orders and send then to the service whjich will extract cancelable orders and
   * forward them to the Cancel Action
   */
  public cancelAll() {
    const orders = [];
    this.gridOptions.api.forEachNodeAfterFilter((rowNode) => orders.push(rowNode.data));
    this.algoContextMenuService.cancelAllOrders(orders);
  }

  public get algoFilterType(): string {
    return this.amData.algoFilterType;
  }

  /**
   * Change the current algo filter  type
   * @param type
   */
  public set algoFilterType(filterTypeLabel: string) {
    this.hasAlgoFilter = filterTypeLabel !== this.algoFilterTypes[2];
    this.hasBMAlgoFilter = filterTypeLabel !== this.algoFilterTypes[1];

    this.amData.algoFilterType = filterTypeLabel;

    this.gridOptions.api?.onFilterChanged();
    this.afterFilteringCompleted();
    this.updateActionAvailability();
  }

  /**
   * Enable/disable the Cancel and Pause action buttons based on current privileges and
   * the select filter ordert state and filter order type
   */
  private updateActionAvailability() {
    const completedOnly = this.stateFilter === AlgoStateFilterType.COMPLETED;
    this.amData.canPauseAll = !completedOnly && this.hasPauseAll;
    if (completedOnly) {
      this.amData.canCancelAll = false;
    } else if (this.hasAlgoFilter && this.hasBMAlgoFilter) {
      this.amData.canCancelAll = this.hasCancelAlgos || this.hasCancelBMAlgos;
    } else if (this.hasAlgoFilter) {
      this.amData.canCancelAll = this.hasCancelAlgos;
    } else {
      this.amData.canCancelAll = this.hasCancelBMAlgos;
    }
  }
}
